import { CheckCircle2, Coins, Hash, Plus, TrendingDown, TrendingUp } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SheetFooter } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { UseCreateTransactionWizardReturn } from "./use-create-transaction-wizard";

interface StepSuccessProps {
  wizard: UseCreateTransactionWizardReturn;
  onClose: () => void;
  onCreateAnother: () => void;
}

function StatCard({
  label,
  value,
  icon: Icon,
  variant = "default",
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  variant?: "default" | "success" | "warning" | "danger";
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-lg border p-4",
        variant === "success" && "border-emerald-500/20 bg-emerald-500/10",
        variant === "warning" && "border-amber-500/20 bg-amber-500/10",
        variant === "danger" && "border-red-500/20 bg-red-500/10",
        variant === "default" && "border-border bg-muted/30",
      )}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={cn(
            "size-4",
            variant === "success" && "text-emerald-600",
            variant === "warning" && "text-amber-600",
            variant === "danger" && "text-red-500",
            variant === "default" && "text-muted-foreground",
          )}
        />
        <span
          className={cn(
            "text-xs font-medium",
            variant === "success" && "text-emerald-700",
            variant === "warning" && "text-amber-700",
            variant === "danger" && "text-red-600",
            variant === "default" && "text-muted-foreground",
          )}
        >
          {label}
        </span>
      </div>
      <span
        className={cn(
          "text-xl font-bold tabular-nums",
          variant === "success" && "text-emerald-600",
          variant === "warning" && "text-amber-600",
          variant === "danger" && "text-red-500",
          variant === "default" && "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function StepSuccess({ wizard, onClose, onCreateAnother }: StepSuccessProps) {
  const { t } = useTranslation();
  const { calculation, createdTransaction } = wizard;

  const transactionType = createdTransaction?.transaction_type ?? "purchase";
  const isPurchaseType = transactionType === "purchase";
  const isDirectSpendingType = transactionType === "direct_spending";
  const isEarningType = [
    "purchase",
    "bonus",
    "manual_adjustment",
    "refund",
    "signup_bonus",
  ].includes(transactionType);

  const pointsChange = calculation?.points ?? createdTransaction?.points ?? 0;
  const newBalance = createdTransaction?.loyalty_account?.current_points;
  const userName = createdTransaction?.user?.name || "User";

  return (
    <>
      <div className="space-y-6">
        {/* Success Header */}
        <div className="flex flex-col items-center justify-center py-6 space-y-4">
          <div className="flex size-16 items-center justify-center rounded-full bg-emerald-500/10">
            <CheckCircle2 className="size-8 text-emerald-600" />
          </div>
          <div className="text-center space-y-1">
            <h3 className="text-lg font-semibold">{t("transactions.create.transaction_created")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("transactions.create.transaction_recorded")}
            </p>
          </div>
        </div>

        {/* User Info */}
        <div>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Avatar name={userName} className="size-12 text-base" />
              <div className="space-y-0.5">
                <h2 className="text-base font-semibold text-foreground">{userName}</h2>
                {createdTransaction?.loyalty_account && (
                  <p className="font-mono text-xs text-muted-foreground">
                    {createdTransaction.loyalty_account.code}
                  </p>
                )}
              </div>
            </div>
            <Badge
              className={cn(
                "text-xs font-medium",
                isPurchaseType &&
                  "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10",
                isDirectSpendingType &&
                  "border-amber-500/20 bg-amber-500/10 text-amber-700 hover:bg-amber-500/10",
                !isPurchaseType && !isDirectSpendingType && "bg-muted text-foreground",
              )}
            >
              {transactionType.replace("_", " ")}
            </Badge>
          </div>

          {/* New Balance display */}
          {newBalance !== undefined && (
            <div className="mt-4 flex items-center justify-between rounded-lg bg-muted/60 px-4 py-3">
              <span className="text-sm text-muted-foreground">{t("transactions.create.new_balance")}</span>
              <div className="flex items-baseline gap-1.5">
                <Coins className="size-4 text-foreground" />
                <span className="text-lg font-bold tabular-nums text-foreground">
                  {newBalance.toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground">{t("common.pts")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Points Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label={isEarningType ? t("transactions.create.earned") : t("transactions.create.spent")}
            value={isEarningType ? `+${pointsChange.toLocaleString()}` : `${pointsChange.toLocaleString()}`}
            icon={isEarningType ? TrendingUp : TrendingDown}
            variant={isEarningType ? "success" : "warning"}
          />
          <StatCard
            label={t("transactions.details.transaction")}
            value={`#${createdTransaction?.code ?? "-"}`}
            icon={Hash}
            variant="default"
          />
        </div>
      </div>

      <SheetFooter>
        <Button type="button" variant="outline" onClick={onCreateAnother}>
          <Plus className="mr-2 h-4 w-4" />
          {t("transactions.create.create_another")}
        </Button>
        <Button type="button" onClick={onClose}>
          {t("transactions.create.close")}
        </Button>
      </SheetFooter>
    </>
  );
}
